<!--
  活动规则页（?rules=true）
  规则正文来自帮助中心文章 superbirthdayrewards；英语/韩语内容为空时回退繁体。
-->
<script lang="ts">
  import { onMount } from "svelte";
  import { _ as t } from "svelte-i18n";
  import NavBar from "../../libs/components/NavBar.svelte";
  import { isEmbedded, shouldShowNavBar } from "../../libs/chief-bridge";
  import {
    langEnumToLangHeader,
    LangEnums,
    StorageKeys,
    storageModel,
  } from "../../libs/storage-manager";
  import { bridge } from "../lib/bridge";
  import { fetchHelpCenterArticle, RULES_SLUG } from "../lib/api";
  import { sanitizeArticleHtml } from "../lib/sanitize-html";

  const showNavBar = shouldShowNavBar();
  const isEmbeddedApp = isEmbedded();

  let articleTitle = $state("");
  let rulesHtml = $state("");
  let loading = $state(true);

  /** 站内读取 localStorage.language；站外读取 URL 的 lan/lang 参数。 */
  function getArticleLanguage(): LangEnums {
    if (isEmbeddedApp) {
      return storageModel[StorageKeys.language] || LangEnums.zhHK;
    }
    const params = new URLSearchParams(window.location.search);
    const language = params.get("lan") || params.get("lang");
    if (Object.values(LangEnums).includes(language as LangEnums)) {
      return language as LangEnums;
    }
    return LangEnums.zhHK;
  }

  onMount(async () => {
    try {
      const language = getArticleLanguage();
      const langHeader = langEnumToLangHeader(language);
      let res = await fetchHelpCenterArticle(RULES_SLUG, langHeader);

      if (
        (language === LangEnums.enUS || language === LangEnums.koKR) &&
        !res.data?.article?.content
      ) {
        res = await fetchHelpCenterArticle(RULES_SLUG, "zh_HK");
      }

      if (res.code === 0 && res.data?.article) {
        const { article } = res.data;
        articleTitle = article.title || "";
        rulesHtml = sanitizeArticleHtml(article.content || "");
      }
    } catch (e) {
      console.error("fetchHelpCenterArticle error:", e);
    } finally {
      loading = false;
    }
  });

  /** 返回首页：去掉 rules 参数，由 App 监听 popstate 切回 Home */
  function handleBack(): boolean {
    const url = new URL(window.location.href);
    url.searchParams.delete("rules");
    window.history.pushState({}, "", url);
    window.dispatchEvent(new PopStateEvent("popstate"));
    return false;
  }

  function handleClose() {
    bridge.send("Route_HomePage");
  }
</script>

<svelte:head>
  <title>{articleTitle}</title>
</svelte:head>

<NavBar
  title={articleTitle}
  scrollFade={false}
  lightBgColor="#ffffff"
  lightTextColor="#000000"
  darkBgColor="#ffffff"
  darkTextColor="#000000"
  onBack={handleBack}
  onClose={handleClose}
  class="bg-white"
>
  <!-- NavBar 高度是真实 px，用内联样式避开 px2rem -->
  <div
    class="rules-content min-h-screen bg-white px-[16px] py-[16px] text-[14px] leading-[1.8] text-[#333]"
    style={showNavBar
      ? "padding-top: var(--nav-bar-height, 44px);"
      : "padding-top: 44px;"}
  >
    {#if !showNavBar}
      <!-- 站外没有 NavBar，补一个返回条 -->
      <div class="browser-bar">
        <button type="button" onclick={handleBack} aria-label="返回">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M15 19L8 12L15 5"
              stroke="#000000"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
        <span>{articleTitle}</span>
      </div>
    {/if}

    {#if loading}
      <p class="text-center text-[#999]">Loading...</p>
    {:else if rulesHtml}
      <!-- ⚠️ 安全关键：rulesHtml 必须经过 sanitizeArticleHtml() 处理后才能在此渲染。
           如需修改赋值逻辑，请确保仍通过 sanitize-html.ts 中的 sanitizeArticleHtml() 过滤。 -->
      {@html rulesHtml}
    {:else}
      <p class="text-center text-[#999]">{$t("no_rules")}</p>
    {/if}
  </div>
</NavBar>

<style>
  .browser-bar {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 50;
    display: flex;
    align-items: center;
    height: 44px;
    max-width: 640px;
    margin: 0 auto;
    padding: 0 16px;
    background: #ffffff;
  }

  .browser-bar button {
    display: flex;
    width: 24px;
    height: 24px;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    background: none;
  }

  .browser-bar span {
    position: absolute;
    left: 60px;
    right: 60px;
    overflow: hidden;
    font-size: 18px;
    font-weight: 600;
    color: #000000;
    text-align: center;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .rules-content :global(p) {
    margin: 0 0 8px;
    word-break: break-word;
  }

  .rules-content :global(h1),
  .rules-content :global(h2),
  .rules-content :global(h3) {
    margin: 16px 0 8px;
    font-weight: bold;
  }

  .rules-content :global(h1) {
    font-size: 20px;
  }

  .rules-content :global(h2) {
    font-size: 17px;
  }

  .rules-content :global(h3) {
    font-size: 15px;
  }

  .rules-content :global(ul),
  .rules-content :global(ol) {
    margin: 0 0 8px;
    padding-left: 20px;
    list-style: revert;
  }

  .rules-content :global(li) {
    margin-bottom: 4px;
  }

  .rules-content :global(a) {
    color: #3e5bdb;
    text-decoration: underline;
    word-break: break-all;
  }

  .rules-content :global(table) {
    width: 100%;
    margin: 8px 0;
    border-collapse: collapse;
  }

  .rules-content :global(th),
  .rules-content :global(td) {
    padding: 6px 8px;
    border: 1px solid #e5e5e5;
    font-size: 13px;
  }

  .rules-content :global(th) {
    background: #f5f5f5;
    font-weight: 600;
  }

  .rules-content :global(img) {
    max-width: 100%;
    height: auto;
  }
</style>
